import { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Textarea } from "@chakra-ui/react";
import { uploadRosterToHie } from "../../../api/internal";
import DrawerForm from "../../shared/form/Drawer";
import { Input } from "../../shared/form/Input";
import { Label } from "../../shared/Label";
import useMetriportToast from "../../shared/toast";
import { captureAndDisplayError } from "../../shared/util";

const formSchema = z.object({
  hieName: z.string(),
  sftpConfig: z
    .string()
    .optional()
    .transform(value => {
      if (!value?.trim()) return undefined;
      return value;
    }),
});
type FormData = z.infer<typeof formSchema>;

export default function RosterUploadForm({
  isOpen,
  onClose,
  cxId,
}: {
  isOpen: boolean;
  onClose: () => void;
  cxId: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useMetriportToast();

  async function handleSubmit(data: FormData) {
    setIsSubmitting(true);
    try {
      await uploadRosterToHie({
        cxId,
        hieName: data.hieName,
        sftpConfig: data.sftpConfig,
      });
      toast.success({ title: "Roster uploaded" });
      onClose();
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      const errorMsg =
        axiosError?.response?.data?.detail ?? axiosError?.message ?? "Failed to upload roster";
      toast.error({ title: errorMsg, duration: 10000 });
      captureAndDisplayError({
        error,
        msg: errorMsg,
        context: "uploadRoster",
        captureMsg: "Upload roster failed",
        toast,
        hideDisplay: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }
  const sftpConfigPlaceholder = [
    "{",
    '  "host": "",',
    '  "port": "",',
    '  "username": "",',
    '  "remotePath": ""',
    "}",
  ].join("\n");

  return (
    <DrawerForm<FormData>
      title="Upload ADT Roster"
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onClose={onClose}
      resolver={zodResolver(formSchema)}
      btSaveId="roster-upload-save"
      btCloseId="roster-upload-close"
    >
      {({ register, formState: { errors } }) => (
        <Box>
          <Input
            {...register("hieName")}
            label="State HIE Name"
            error={errors.hieName}
            placeholder="Bamboo"
          />
          <Label>Optional SFTP Config for testing (JSON)</Label>
          <Textarea
            {...register("sftpConfig")}
            fontFamily="mono"
            fontSize="sm"
            rows={10}
            placeholder={sftpConfigPlaceholder}
          />
        </Box>
      )}
    </DrawerForm>
  );
}
